/**
 * Word Export Service for AI AutoMarker
 * Generates Word documents for quiz questions and answers
 * 
 * Creates two documents:
 * 1. QuizName_Questions.docx - Student version (questions only)
 * 2. QuizName_Answers.docx - Teacher version (questions with mark scheme answers in red)
 */

import {
  Document,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  PageBreak,
  convertInchesToTwip,
} from "docx";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "./apiBase";
import { auth } from "../firebaseConfig";
import {
  isMultipleChoiceQuestion,
  mcqOptionsForDisplay,
  resolvedCorrectOptionForTeacher,
} from "./quizQuestionCompatibility";

export const formatMcqWordAnswer = (question) => {
  const correctOption = resolvedCorrectOptionForTeacher(question);
  return correctOption
    ? `${correctOption.label}. ${correctOption.text}`
    : "Correct option unavailable";
};

/**
 * Fetches an image from URL and converts it to ArrayBuffer
 * @param {string} url - The image URL
 * @returns {Promise<{data: ArrayBuffer, width: number, height: number} | null>}
 */
const fetchImageAsArrayBuffer = async (url, question) => {
  let temporaryUrl = "";
  try {
    if (!url && !question?.questionMedia) return null;
    let response;
    if (question?.questionMedia) {
      const token = await auth.currentUser?.getIdToken();
      if (!token || !question.id) return null;
      const base = API_BASE_URL;
      response = await fetch(`${base}/api/question-media/deliver`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ questionId: question.id, context: { type: "question-owner" } }),
      });
    } else {
      response = await fetch(url);
    }
    if (!response.ok) {
      console.warn("Failed to fetch question image for export");
      return null;
    }
    
    const blob = await response.blob();
    if (question?.questionMedia) temporaryUrl = URL.createObjectURL(blob);
    const arrayBuffer = await blob.arrayBuffer();
    
    // Get image dimensions
    const img = new Image();
    const imageDimensions = await new Promise((resolve) => {
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        resolve({ width: 400, height: 300 }); // Default dimensions
      };
      img.src = temporaryUrl || url;
    });
    
    return {
      data: arrayBuffer,
      width: imageDimensions.width,
      height: imageDimensions.height,
    };
  } catch (error) {
    console.warn("Error fetching question image for export:", error);
    return null;
  } finally {
    if (temporaryUrl) URL.revokeObjectURL(temporaryUrl);
  }
};

/**
 * Calculates scaled dimensions to fit within max width while preserving aspect ratio
 * @param {number} originalWidth 
 * @param {number} originalHeight 
 * @param {number} maxWidth - Maximum width in pixels (default 450 for printing)
 * @returns {{width: number, height: number}}
 */
const calculateScaledDimensions = (originalWidth, originalHeight, maxWidth = 450) => {
  if (originalWidth <= maxWidth) {
    return { width: originalWidth, height: originalHeight };
  }
  
  const aspectRatio = originalHeight / originalWidth;
  return {
    width: maxWidth,
    height: Math.round(maxWidth * aspectRatio),
  };
};

/**
 * Creates the document header section with quiz metadata
 * @param {Object} quizData - Quiz metadata
 * @param {boolean} isAnswerDoc - Whether this is the answer document
 * @returns {Paragraph[]}
 */
const createDocumentHeader = (quizData, isAnswerDoc) => {
  const headerParagraphs = [];
  
  // Title
  headerParagraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: quizData.quizCode || "Test",
          bold: true,
          size: 48, // 24pt
        }),
        new TextRun({
          text: isAnswerDoc ? " - ANSWER KEY" : " - Student Paper",
          bold: true,
          size: 48,
          color: isAnswerDoc ? "CC0000" : "000000",
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    })
  );
  
  // Subject, Topic, Level, Spec Code (if available)
  const metadataItems = [];
  if (quizData.subject) metadataItems.push(`Subject: ${quizData.subject}`);
  if (quizData.topic) metadataItems.push(`Topic: ${quizData.topic}`);
  if (quizData.level) metadataItems.push(`Level: ${quizData.level}`);
  if (quizData.specCode) metadataItems.push(`Spec Code: ${quizData.specCode}`);
  
  if (metadataItems.length > 0) {
    headerParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: metadataItems.join(" | "),
            size: 24, // 12pt
            italics: true,
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      })
    );
  }
  
  // Total marks and number of questions
  headerParagraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Total Questions: ${quizData.totalQuestions || 0}`,
          size: 24,
          bold: true,
        }),
        new TextRun({
          text: "    |    ",
          size: 24,
        }),
        new TextRun({
          text: `Total Marks: ${quizData.totalMarks || 0}`,
          size: 24,
          bold: true,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 300 },
    })
  );
  
  // Horizontal line
  headerParagraphs.push(
    new Paragraph({
      border: {
        bottom: {
          color: "000000",
          style: BorderStyle.SINGLE,
          size: 12,
        },
      },
      spacing: { after: 300 },
    })
  );
  
  // Student info section (only for student paper)
  if (!isAnswerDoc) {
    headerParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Student Name: ___________________________________",
            size: 24,
          }),
        ],
        spacing: { after: 150 },
      })
    );
    
    headerParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Class: _________________    Date: _________________",
            size: 24,
          }),
        ],
        spacing: { after: 400 },
      })
    );
  }
  
  return headerParagraphs;
};

/**
 * Creates a question section for the document
 * @param {Object} question - Question data
 * @param {number} questionNumber - Question number
 * @param {boolean} includeAnswer - Whether to include the mark scheme answer
 * @param {Object|null} imageData - Image data if available
 * @returns {Paragraph[]}
 */
const createQuestionSection = (question, questionNumber, includeAnswer, imageData) => {
  const questionParagraphs = [];
  
  // Question number with marks
  const questionText = question.questionText || "[Image-based question - see above]";
  const marks = question.maxMark || 0;
  
  questionParagraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Question ${questionNumber}`,
          bold: true,
          size: 28, // 14pt
        }),
        new TextRun({
          text: ` [${marks} Mark${marks !== 1 ? 's' : ''}]`,
          bold: true,
          size: 24,
          color: "666666",
        }),
      ],
      spacing: { before: 300, after: 150 },
    })
  );
  
  // Question image (if available) - placed ABOVE question text for exam-style layout
  if (imageData) {
    const scaledDimensions = calculateScaledDimensions(
      imageData.width,
      imageData.height,
      450
    );
    
    questionParagraphs.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: imageData.data,
            transformation: {
              width: scaledDimensions.width,
              height: scaledDimensions.height,
            },
          }),
        ],
        spacing: { after: 150 },
      })
    );
  }
  
  // Question text
  questionParagraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: questionText,
          size: 24, // 12pt
        }),
      ],
      spacing: { after: 200 },
    })
  );

  const isMultipleChoice = isMultipleChoiceQuestion(question);
  const multipleChoiceOptions = mcqOptionsForDisplay(question);
  if (isMultipleChoice) {
    multipleChoiceOptions.forEach((option) => {
      questionParagraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${option.label}. `,
              bold: true,
              size: 24,
              color: "08758A",
            }),
            new TextRun({ text: option.text, size: 24 }),
          ],
          indent: { left: convertInchesToTwip(0.25) },
          spacing: { after: 80 },
        })
      );
    });
  }
  
  // Answer space for student paper OR mark scheme for answer doc
  if (includeAnswer) {
    const answerHeading = isMultipleChoice ? "Correct Answer:" : "Mark Scheme Answer:";
    questionParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: answerHeading,
            bold: true,
            size: 24,
            color: "CC0000",
          }),
        ],
        spacing: { before: 200, after: 100 },
      })
    );

    const answerText = isMultipleChoice
      ? formatMcqWordAnswer(question)
      : (question.markSchemeAnswer || "No mark scheme provided");
    // Split by newlines to preserve formatting
    const answerLines = answerText.split('\n');
    
    answerLines.forEach((line, index) => {
      questionParagraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: line || " ", // Use space for empty lines
              size: 24,
              color: "CC0000",
            }),
          ],
          spacing: { after: index === answerLines.length - 1 ? 200 : 50 },
        })
      );
    });
  } else if (isMultipleChoice) {
    questionParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Select one answer.",
            bold: true,
            size: 22,
            color: "666666",
          }),
        ],
        spacing: { before: 150, after: 200 },
      })
    );
  } else {
    // Answer space for student
    questionParagraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: "Answer:",
            bold: true,
            size: 24,
          }),
        ],
        spacing: { before: 200, after: 100 },
      })
    );
    
    // Blank lines for student to write answer
    const blankLineCount = Math.max(3, Math.min(marks * 2, 10)); // 2 lines per mark, min 3, max 10
    for (let i = 0; i < blankLineCount; i++) {
      questionParagraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "_______________________________________________________________________________",
              size: 24,
              color: "CCCCCC",
            }),
          ],
          spacing: { after: 100 },
        })
      );
    }
  }
  
  // Add separator line between questions
  questionParagraphs.push(
    new Paragraph({
      border: {
        bottom: {
          color: "CCCCCC",
          style: BorderStyle.SINGLE,
          size: 6,
        },
      },
      spacing: { after: 300 },
    })
  );
  
  return questionParagraphs;
};

/**
 * Generates a Word document for the quiz
 * @param {Object} quizData - Quiz metadata
 * @param {Array} questions - Array of question objects in correct order
 * @param {boolean} isAnswerDoc - Whether to generate answer document
 * @returns {Promise<Document>}
 */
const generateQuizDocument = async (quizData, questions, isAnswerDoc) => {
  const sections = [];
  
  // Fetch all images in parallel
  const imagePromises = questions.map((q) =>
    (q.questionImageURL || q.questionMedia) ? fetchImageAsArrayBuffer(q.questionImageURL, q) : Promise.resolve(null)
  );
  const imageResults = await Promise.all(imagePromises);
  
  // Create document content
  const documentChildren = [];
  
  // Add header
  const headerParagraphs = createDocumentHeader(quizData, isAnswerDoc);
  documentChildren.push(...headerParagraphs);
  
  // Add each question
  questions.forEach((question, index) => {
    const questionParagraphs = createQuestionSection(
      question,
      index + 1,
      isAnswerDoc,
      imageResults[index]
    );
    documentChildren.push(...questionParagraphs);
  });
  
  // Create the document
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: "Calibri",
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.75),
              right: convertInchesToTwip(0.75),
              bottom: convertInchesToTwip(0.75),
              left: convertInchesToTwip(0.75),
            },
          },
        },
        children: documentChildren,
      },
    ],
  });
  
  return doc;
};

/**
 * Exports quiz to Word documents (both questions and answers)
 * @param {Object} quizData - Quiz metadata including quizCode, subject, topic, level, specCode
 * @param {Array} questions - Array of question objects in correct order
 * @param {Function} onProgress - Optional callback for progress updates
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const exportQuizToWord = async (quizData, questions, onProgress) => {
  try {
    if (!questions || questions.length === 0) {
      return { success: false, error: "No questions to export" };
    }
    
    // Calculate totals
    const totalMarks = questions.reduce((sum, q) => sum + (parseFloat(q.maxMark) || 0), 0);
    const enhancedQuizData = {
      ...quizData,
      totalQuestions: questions.length,
      totalMarks: totalMarks,
    };
    
    // Sanitize quiz code for filename
    const sanitizedQuizCode = (quizData.quizCode || "Test").replace(/[^a-zA-Z0-9-_]/g, "_");
    
    if (onProgress) onProgress("Generating Questions document...");
    
    // Generate Questions document
    const questionsDoc = await generateQuizDocument(enhancedQuizData, questions, false);
    const questionsBlob = await generateDocxBlob(questionsDoc);
    
    if (onProgress) onProgress("Generating Answers document...");
    
    // Generate Answers document
    const answersDoc = await generateQuizDocument(enhancedQuizData, questions, true);
    const answersBlob = await generateDocxBlob(answersDoc);
    
    if (onProgress) onProgress("Downloading files...");
    
    // Download both documents
    saveAs(questionsBlob, `${sanitizedQuizCode}_Questions.docx`);
    
    // Small delay between downloads to ensure both trigger
    await new Promise(resolve => setTimeout(resolve, 500));
    
    saveAs(answersBlob, `${sanitizedQuizCode}_Answers.docx`);
    
    return { success: true };
  } catch (error) {
    console.error("Error exporting quiz to Word:", error);
    return { success: false, error: error.message || "Failed to generate Word documents" };
  }
};

/**
 * Generates a blob from a docx Document
 * @param {Document} doc - The docx Document object
 * @returns {Promise<Blob>}
 */
const generateDocxBlob = async (doc) => {
  const { Packer } = await import("docx");
  return await Packer.toBlob(doc);
};

/**
 * Fetches quiz questions from Firestore
 * @param {Object} db - Firestore database instance
 * @param {Array} questionIds - Array of question IDs
 * @returns {Promise<Array>} - Array of question objects in correct order
 */
export const fetchQuizQuestions = async (db, questionIds) => {
  if (!questionIds || questionIds.length === 0) {
    return [];
  }
  
  try {
    const { collection, query, where, getDocs, documentId } = await import("firebase/firestore");
    
    const questionMap = {};
    
    // Fetch in batches of 10 (Firestore limit for 'in' queries)
    for (let i = 0; i < questionIds.length; i += 10) {
      const batch = questionIds.slice(i, i + 10);
      const questionsRef = collection(db, "questions");
      const questionsQuery = query(questionsRef, where(documentId(), "in", batch));
      const questionsSnapshot = await getDocs(questionsQuery);
      
      questionsSnapshot.docs.forEach((doc) => {
        questionMap[doc.id] = {
          id: doc.id,
          ...doc.data(),
        };
      });
    }
    
    // Return questions in the correct order (same as questionIds)
    return questionIds.map((id) => questionMap[id]).filter((q) => q);
  } catch (error) {
    console.error("Error fetching quiz questions:", error);
    throw error;
  }
};

/**
 * Fetches a complete quiz with its questions
 * @param {Object} db - Firestore database instance
 * @param {string} quizId - Quiz document ID or quiz code
 * @returns {Promise<{quiz: Object, questions: Array}>}
 */
export const fetchQuizWithQuestions = async (db, quizId) => {
  try {
    const { doc, getDoc, collection, query, where, getDocs } = await import("firebase/firestore");
    
    let quizData = null;
    
    // First try to get by document ID
    const quizDocRef = doc(db, "quizzes", quizId);
    const quizDocSnap = await getDoc(quizDocRef);
    
    if (quizDocSnap.exists()) {
      quizData = { id: quizDocSnap.id, ...quizDocSnap.data() };
    } else {
      // Try to get by quiz code
      const quizQuery = query(collection(db, "quizzes"), where("quizCode", "==", quizId));
      const quizSnapshot = await getDocs(quizQuery);
      
      if (!quizSnapshot.empty) {
        const quizDoc = quizSnapshot.docs[0];
        quizData = { id: quizDoc.id, ...quizDoc.data() };
      }
    }
    
    if (!quizData) {
      throw new Error(`Test not found: ${quizId}`);
    }
    
    // Fetch questions
    const questions = await fetchQuizQuestions(db, quizData.questionIds);
    
    return { quiz: quizData, questions };
  } catch (error) {
    console.error("Error fetching quiz with questions:", error);
    throw error;
  }
};

export default {
  exportQuizToWord,
  fetchQuizQuestions,
  fetchQuizWithQuestions,
};
